/**
 * Compact 9-segment profile completion control + dropdown list with navigation.
 * Used on profile subpages (next to Back) and list content is reused on User Home.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import {
  PROFILE_COMPLETION_SEGMENT_COUNT,
  deriveProfileCompletion,
  getIncompleteNavItems,
  isProfileCompletionFullyComplete,
  type ProfileCompletionDerived,
} from '@/constants/profileCompletionSegments';

export function ProfileCompletionMiniBar({
  state,
  size = 'compact',
}: {
  state: ProfileCompletionDerived;
  size?: 'compact' | 'comfortable';
}) {
  const showKnown = state.isReady && !state.isIndeterminate;
  const fractionLabel =
    !state.isReady || state.isIndeterminate
      ? `—/${PROFILE_COMPLETION_SEGMENT_COUNT}`
      : `${state.completedCount}/${PROFILE_COMPLETION_SEGMENT_COUNT}`;

  const incompleteCount = showKnown
    ? Math.max(0, PROFILE_COMPLETION_SEGMENT_COUNT - state.completedCount)
    : null;

  const sectionsSummary =
    !state.isReady
      ? 'Checking profile…'
      : state.isIndeterminate
        ? 'Completion unavailable'
        : incompleteCount === 0
          ? 'All sections complete'
          : incompleteCount === 1
            ? '1 section to complete'
            : `${incompleteCount} sections to complete`;

  const isComfort = size === 'comfortable';

  const fillPct = showKnown
    ? (state.completedCount / PROFILE_COMPLETION_SEGMENT_COUNT) * 100
    : 0;

  return (
    <>
      <div
        className={cn(
          'flex min-w-0 items-center',
          isComfort ? 'mb-2 gap-2.5' : 'mb-1.5 gap-2 sm:gap-2.5'
        )}
      >
        <div
          className={cn(
            'relative min-w-0 flex-1 overflow-hidden rounded-full bg-white/10',
            isComfort ? 'h-2' : 'h-1.5 sm:h-2'
          )}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-0 bg-amber-400/80 transition-[width] duration-300 ease-out',
              state.completedCount >= PROFILE_COMPLETION_SEGMENT_COUNT
                ? 'rounded-full'
                : 'rounded-l-full',
              showKnown && state.completedCount > 0 && state.completedCount < PROFILE_COMPLETION_SEGMENT_COUNT
                ? 'min-w-[6px]'
                : null
            )}
            style={{ width: `${fillPct}%` }}
          />
          <div
            className="pointer-events-none absolute inset-0 flex"
            aria-hidden
          >
            {Array.from({ length: PROFILE_COMPLETION_SEGMENT_COUNT }, (_, i) => (
              <div
                key={i}
                className="min-w-0 flex-1 border-r border-black/25 last:border-r-0"
              />
            ))}
          </div>
        </div>
        <span
          className={cn(
            'shrink-0 font-black tabular-nums tracking-widest text-white',
            isComfort ? 'text-xs' : 'text-[10px] sm:text-xs'
          )}
        >
          {fractionLabel}
        </span>
      </div>
      <p
        className={cn(
          'font-black uppercase tracking-widest text-[#8E8E93]',
          isComfort ? 'text-[10px]' : 'text-[9px] sm:text-[10px]'
        )}
      >
        {sectionsSummary}
      </p>
    </>
  );
}

export function ProfileCompletionListContent({
  state,
  onNavigate,
}: {
  state: ProfileCompletionDerived;
  onNavigate?: (path: string) => void;
}) {
  if (!state.isReady) {
    return <p className="text-sm text-[#8E8E93]">Loading profile…</p>;
  }
  if (state.isIndeterminate) {
    return <p className="text-sm text-[#8E8E93]">Completion details unavailable.</p>;
  }
  const items = getIncompleteNavItems(state);
  if (items.length === 0) {
    return <p className="text-sm font-medium text-amber-400/90">All profile blocks complete.</p>;
  }
  if (!onNavigate) {
    return (
      <ul className="space-y-2">
        {items.map((item) => (
          <li
            key={item.segmentId}
            className="border-l-2 border-amber-400/50 pl-2.5 text-sm leading-snug text-white/90"
          >
            {item.label}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.segmentId}>
          <button
            type="button"
            onClick={() => onNavigate(item.path)}
            className="w-full rounded-lg border border-transparent px-1.5 py-2 text-left text-sm text-white/90 transition-colors hover:border-amber-400/25 hover:bg-white/[0.04] hover:text-amber-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
          >
            {item.label}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Subtle top-right ping for incomplete checklist-style cards. */
export function ProfileIncompleteCornerPing({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute right-4 top-4 z-[1] md:right-5 md:top-5',
        className
      )}
      aria-hidden
    >
      <span className="sr-only">Section incomplete</span>
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/35" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.5)]" />
      </span>
    </div>
  );
}

type ProfileCompletionBlocksControlProps = {
  className?: string;
};

const PANEL_MAX_WIDTH_PX = 288; // 18rem
const PANEL_VIEWPORT_MARGIN = 12;

export function ProfileCompletionBlocksControl({ className }: ProfileCompletionBlocksControlProps) {
  const navigate = useNavigate();
  const { data: artistDetails, ensureFetched } = useArtistDetailsStore();
  const [open, setOpen] = useState(false);
  const [panelLayout, setPanelLayout] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void ensureFetched();
  }, [ensureFetched]);

  const state = useMemo(() => deriveProfileCompletion(artistDetails), [artistDetails]);

  const updatePanelLayout = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn || typeof window === 'undefined') return;
    const rect = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(PANEL_MAX_WIDTH_PX, vw - 2 * PANEL_VIEWPORT_MARGIN);
    let left = rect.right - width;
    left = Math.max(
      PANEL_VIEWPORT_MARGIN,
      Math.min(left, vw - width - PANEL_VIEWPORT_MARGIN)
    );
    setPanelLayout({
      top: rect.bottom + 8,
      left,
      width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelLayout(null);
      return;
    }
    updatePanelLayout();
    window.addEventListener('scroll', updatePanelLayout, true);
    window.addEventListener('resize', updatePanelLayout);
    return () => {
      window.removeEventListener('scroll', updatePanelLayout, true);
      window.removeEventListener('resize', updatePanelLayout);
    };
  }, [open, updatePanelLayout]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  if (isProfileCompletionFullyComplete(state)) {
    return null;
  }

  const dropdown =
    open &&
    panelLayout &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        ref={panelRef}
        className="fixed z-[10000] rounded-2xl border border-[#3A3A3C]/60 bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] p-4 shadow-2xl"
        style={{
          top: panelLayout.top,
          left: panelLayout.left,
          width: panelLayout.width,
        }}
      >
        <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-[#8E8E93]">
          Still needed
        </p>
        <ProfileCompletionListContent
          state={state}
          onNavigate={(path) => {
            navigate(path);
            setOpen(false);
          }}
        />
      </div>,
      document.body
    );

  return (
    <div className={cn('relative shrink-0', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-w-[9.5rem] rounded-xl border border-[#3A3A3C]/60 bg-white/[0.03] px-3 py-2 text-left transition-colors hover:border-[#3A3A3C] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 sm:min-w-[11rem]"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <ProfileCompletionMiniBar state={state} />
      </button>
      {dropdown}
    </div>
  );
}
